<div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="releaseModalTitle">
  <div class="modal-box device-release-modal-box">
    <!-- Pinned Header -->
    <div class="modal-header">
      <div class="modal-title-group">
        <h3 id="releaseModalTitle">Release App to Device</h3>
        <p class="modal-subtitle">
          Targeting CoinSing terminal <strong>{{ deviceName }}</strong> ({{ deviceId }})
        </p>
      </div>
      <button type="button" class="close-btn" (click)="closeModal()" aria-label="Close dialog" [disabled]="isPublishing">×</button>
    </div>

    <!-- Modal Body -->
    <div class="modal-body">
      <!-- Loading Spinner -->
      <div *ngIf="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p class="loading-text">Loading CoinSing versions and device telemetry...</p>
      </div>

      <div *ngIf="!isLoading">
        <!-- Error Banner -->
        <div class="alert-error" role="alert" *ngIf="errorMessage">
          {{ errorMessage }}
        </div>

        <!-- Success Banner -->
        <div class="alert-success" role="status" *ngIf="statusMessage">
          {{ statusMessage }}
        </div>

        <!-- Offline Device Banner (AC-2) -->
        <div class="alert-offline" role="note" *ngIf="!isDeviceOnline">
          <span class="alert-icon">⚠️</span>
          <span>The release is stored and applies after it reconnects and restarts.</span>
        </div>

        <!-- Device Status Overview -->
        <div class="status-overview-card">
          <div class="overview-row">
            <span class="overview-label">Currently Installed:</span>
            <span class="overview-value installed-badge" data-testid="km-installed-version">
              {{ installedVersionDisplay }}
            </span>
          </div>

          <div class="overview-row" *ngIf="deviceTargetVersion">
            <span class="overview-label">Targeted Version:</span>
            <div class="target-version-group">
              <span class="target-ver-text">v{{ deviceTargetVersion }}</span>
              <span *ngIf="updatePill.pillText"
                    [class]="'pill-badge ' + updatePill.pillClass"
                    role="status"
                    [attr.aria-label]="updatePill.ariaLabel"
                    data-testid="km-update-pill">
                {{ updatePill.pillText }}
              </span>
            </div>
            <button type="button" class="btn-refresh-status" (click)="refreshDeviceData()" [disabled]="isRefreshingStatus" title="Refresh device update status">
              <span *ngIf="!isRefreshingStatus">🔄 Refresh Status</span>
              <span *ngIf="isRefreshingStatus" class="spinner-small"></span>
            </button>
          </div>
        </div>

        <!-- Release Selection Form -->
        <div class="form-section">
          <label class="form-label" for="versionSelect">Select CoinSing Version to Target:</label>
          <div class="version-select-row">
            <select id="versionSelect" class="form-select" [value]="selectedVersion?.version" (change)="onSelectVersion($event)">
              <option *ngFor="let ver of appVersionsList" [value]="ver.version">
                v{{ ver.version }}
                <ng-container *ngIf="ver.isRollback"> (older than installed)</ng-container>
                — Published {{ ver.publishedAt ? (ver.publishedAt.toDate ? (ver.publishedAt.toDate() | date:'shortDate') : (ver.publishedAt | date:'shortDate')) : 'N/A' }} by {{ ver.publishedBy }}
              </option>
            </select>
            <button type="button" class="btn-view-notes" (click)="toggleReleaseNotesDrawer(true)" title="View release notes for selected version">
              📝 Release Notes
            </button>
          </div>

          <!-- Direction & Details Card -->
          <div class="details-card" *ngIf="selectedVersion">
            <div class="detail-row">
              <span class="detail-label">Release Direction:</span>
              <span class="direction-badge" [class.direction-rollback]="directionText === 'Rollback'" [class.direction-reinstall]="directionText === 'Reinstall'" [class.direction-upgrade]="directionText === 'Upgrade'">
                {{ directionText }}
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Target Version:</span>
              <span class="detail-value">v{{ selectedVersion.version }}</span>
            </div>
            <div class="detail-row" *ngIf="selectedBuildConfiguration">
              <span class="detail-label">Build Configuration:</span>
              <span class="detail-value">{{ selectedBuildConfiguration }}</span>
            </div>
            <div class="detail-row" *ngIf="selectedGitCommitShort">
              <span class="detail-label">Git Commit:</span>
              <span class="detail-value commit-hash">{{ selectedGitCommitShort }}</span>
            </div>
          </div>

          <!-- Same Version Warning Hint -->
          <p class="same-version-hint" *ngIf="sameVersionHint" role="alert">
            ℹ️ {{ sameVersionHint }}
          </p>

          <!-- Force Update Checkbox -->
          <div class="force-update-container">
            <label class="checkbox-label force-checkbox-label">
              <input type="checkbox" [(ngModel)]="forceUpdate" class="custom-checkbox force-checkbox" />
              <span class="force-label-text">Force Update (re-download package even if hash matches)</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Pinned Footer Actions -->
    <div class="modal-footer">
      <button type="button" class="btn-cancel" (click)="closeModal()" [disabled]="isPublishing">
        Cancel
      </button>
      <button type="button" id="btnReleaseToDevice" class="btn-submit" (click)="onReleaseClick()" [disabled]="isReleaseDisabled" data-testid="km-submit-release">
        <span *ngIf="!isPublishing">📲 Release to This Device</span>
        <span *ngIf="isPublishing">
          <span class="spinner-small"></span> Targeting...
        </span>
      </button>
    </div>

    <!-- Rollback Confirmation In-Page AlertDialog (AC-5) -->
    <div class="alertdialog-backdrop" *ngIf="showRollbackConfirmDialog" role="alertdialog" aria-modal="true" aria-labelledby="rollbackDialogTitle" aria-describedby="rollbackDialogDesc">
      <div class="alertdialog-box">
        <div class="alertdialog-header">
          <h4 id="rollbackDialogTitle" class="rollback-title">⚠️ Confirm Rollback</h4>
        </div>
        <div class="alertdialog-body">
          <p id="rollbackDialogDesc" class="rollback-copy">
            Roll back {{ deviceName || deviceId }} to v{{ selectedVersion?.version }}? {{ deviceName || deviceId }} is running {{ installedVersionDisplay }}. You are about to install an OLDER version, v{{ selectedVersion?.version }}. The kiosk will downgrade the next time it restarts. Older versions may not support settings or features added since v{{ selectedVersion?.version }}.
          </p>
        </div>
        <div class="alertdialog-footer">
          <button type="button" id="btnCancelRollback" class="btn-cancel" (click)="cancelRollbackDialog()">
            Cancel
          </button>
          <button type="button" class="btn-danger" (click)="confirmRollback()">
            Roll back to v{{ selectedVersion?.version }}
          </button>
        </div>
      </div>
    </div>

    <!-- Slide-in Release Notes Drawer (AC-7) -->
    <div class="version-note-drawer" [class.open]="showReleaseNoteDrawer">
      <div class="drawer-header">
        <div class="drawer-title-group">
          <h4 class="drawer-title">
            <span class="drawer-header-icon">📝</span>
            Release Notes — v{{ selectedVersion?.version }}
          </h4>
          <p class="drawer-subtitle" *ngIf="releaseNoteUpdatedAt">
            Updated {{ releaseNoteUpdatedAt | date:'medium' }}
            <span *ngIf="releaseNoteUpdatedBy">by {{ releaseNoteUpdatedBy }}</span>
          </p>
        </div>

        <div class="drawer-header-actions">
          <button type="button" class="btn-drawer-edit" *ngIf="!isEditingNote && !isLoadingNote && canEditNotes" (click)="startEditNote()" title="Edit Release Note">
            ✏️ Edit
          </button>
          <button type="button" class="drawer-close-btn" (click)="closeReleaseNotesDrawer()" title="Close Release Notes">✕</button>
        </div>
      </div>

      <div class="drawer-body">
        <div *ngIf="noteFeedbackMessage" class="alert-success drawer-alert">
          {{ noteFeedbackMessage }}
        </div>
        <div *ngIf="noteErrorMessage" class="alert-error drawer-alert">
          {{ noteErrorMessage }}
        </div>

        <!-- Note Loading -->
        <div *ngIf="isLoadingNote" class="drawer-loading-container">
          <div class="spinner"></div>
          <p class="drawer-loading-text">Loading release notes for v{{ selectedVersion?.version }}...</p>
        </div>

        <!-- Note View Mode -->
        <div *ngIf="!isLoadingNote && !isEditingNote" class="drawer-view-section">
          <div *ngIf="releaseNoteContent" class="note-view-wrapper ql-snow">
            <div class="ql-editor note-view-editor" [innerHTML]="getSanitizedNote()"></div>
          </div>
          <div *ngIf="!releaseNoteContent" class="empty-note-state">
            <div class="empty-note-icon">📋</div>
            <h5 class="empty-note-title">No Release Notes Available</h5>
            <p class="empty-note-desc">
              No release notes have been recorded for CoinSing Version {{ selectedVersion?.version }} yet.
            </p>
            <button type="button" class="btn-create-note" *ngIf="canEditNotes" (click)="startEditNote()">
              ➕ Add Release Note
            </button>
          </div>
          <p class="drawer-immutable-notice">
            Release files cannot be changed. Only release notes can be edited.
          </p>
        </div>

        <!-- Note Edit Mode -->
        <div *ngIf="!isLoadingNote && isEditingNote" class="drawer-edit-section">
          <div class="editor-section-header">
            <span class="editor-section-label">Edit Formatted Release Notes:</span>
          </div>
          <div class="quill-editor-container">
            <quill-editor [(ngModel)]="releaseNoteContent" [modules]="quillConfig" placeholder="Type release highlights, bug fixes, or instructions...">
            </quill-editor>
          </div>
          <div class="drawer-edit-footer">
            <button type="button" class="btn-cancel-edit" (click)="cancelEditNote()" [disabled]="isSavingNote">
              Cancel
            </button>
            <button type="button" class="btn-save-note" (click)="saveReleaseNote()" [disabled]="isSavingNote">
              <span *ngIf="!isSavingNote">💾 Save Release Note</span>
              <span *ngIf="isSavingNote">
                <span class="spinner-small"></span> Saving...
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

